// Справочники: воронки, стадии, сотрудники.
// Данные приходят из ядра (GET /refs → bx_users + crm.dealcategory.list/crm.status.list),
// хардкода названий здесь БОЛЬШЕ НЕТ: поменяли воронку в Bitrix — фронт увидит это сам.
// Если справочник не загрузился, показываем сырой ID («C4:NEW», «#12») — лучше непонятно,
// чем уверенно неправильно.
//
// Логика, которая НЕ зависит от данных Bitrix (правила STAGE_ID), живёт здесь же:
// stageFilter — как собрать фильтр по стадии для запроса в ядро.

import type { RefStage, RefsData } from '../api/types'
import { refId } from './smart'

export interface StageOption {
  /** STAGE_ID целиком («C4:NEW»). Пусто, если воронка не выбрана и код встречается в нескольких. */
  id: string
  /** Код без префикса воронки («NEW»). */
  code: string
  name: string
  /** Название воронки — нужно, когда стадии всех воронок показаны одним списком. */
  categoryName?: string
}

export type StageKind = 'won' | 'lose' | 'work'

export interface RefUserOption {
  id: string
  name: string
  /** false — сотрудник уволен. В старых записях он остаётся ответственным, поэтому не скрываем. */
  active: boolean
}

export interface RefsApi {
  /** false — справочники не пришли, всё показываем сырыми ID. */
  loaded: boolean
  /** Ядро отдало старый кеш воронок/стадий (Bitrix не ответил). */
  dictsStale: boolean
  categories: { id: string; name: string }[]
  /** Сотрудники для фильтра «ответственный»: действующие сначала, уволенные — с пометкой. */
  users: RefUserOption[]
  /** Стадии воронки; без воронки — уникальные коды по всем воронкам. */
  stageOptions(categoryId: string): StageOption[]
  stageName(stageId?: string | null): string
  categoryName(categoryId?: string | null): string
  userName(id?: string | number | null): string
  /**
   * Имя сотрудника из справочника, а если его там нет — своё запасное.
   *
   * Зачем отдельно от `userName`. У него два разных ответа на два разных вопроса:
   * «привязки нет» → «—» и «привязка есть, а сотрудника не знаю» → «#123». Подстановка
   * `userName(id) || свой_вариант` не срабатывает НИКОГДА: обе строки непустые. Из-за этого
   * в инженерном блоке имя инженера из зеркала портала 73 не показывалось ни разу.
   *
   * `fallback` — то, что известно вызывающему (имя из учётки портала). Пустой fallback
   * возвращает поведение `userName`.
   */
  userNameOr(id?: string | number | null, fallback?: string | null): string
  stageKind(stageId?: string | null): StageKind

  // ─── смарт-процессы: свои воронки и стадии у каждого типа ───
  /** Пришли ли стадии этого типа. false — фильтр по стадии показывать нечем. */
  hasItemStages(entityTypeId: number | string): boolean
  itemCategories(entityTypeId: number | string): { id: string; name: string }[]
  /** Стадии типа; при выбранной воронке — только её. `id` всегда полный («DT134_27:NEW»). */
  itemStageOptions(entityTypeId: number | string, categoryId?: string): StageOption[]
  itemStageName(stageId?: string | null): string
  itemCategoryName(entityTypeId: number | string, categoryId?: string | number | null): string
  itemStageKind(stageId?: string | null): StageKind
}

/** «C4:NEW» → «NEW»; без префикса отдаём как есть. */
function stageCode(stageId?: string | null): string {
  const s = String(stageId ?? '')
  return s.includes(':') ? s.split(':').slice(1).join(':') : s
}

const byNum = (a: { sort?: number }, b: { sort?: number }) => (a.sort ?? 0) - (b.sort ?? 0)

function fullName(u: { name: string | null; last_name: string | null; id: number }): string {
  const s = [u.last_name, u.name].filter(Boolean).join(' ').trim()
  return s || `#${u.id}`
}

/** Семантика стадии → как её красить. Пустая семантика — «в работе», это не ошибка. */
function kindOfSemantics(sem?: string | null): StageKind | null {
  if (sem === 'S') return 'won'
  if (sem === 'F') return 'lose'
  if (sem) return 'work'
  return null
}

/** Собрать интерфейс справочников из ответа ядра. `undefined` — справочники недоступны. */
export function makeRefs(data?: RefsData): RefsApi {
  const stages = data?.stages ?? []
  const stageById = new Map<string, RefStage>(stages.map((s) => [s.id, s]))
  const categoryById = new Map((data?.categories ?? []).map((c) => [c.id, c.name]))
  const userById = new Map((data?.users ?? []).map((u) => [String(u.id), fullName(u)]))

  // Стадии смарт-процессов: «тип → стадии». Плоскую карту по stageId собираем один раз —
  // код стадии смарт-процесса уникален глобально (в нём зашиты и тип, и воронка),
  // поэтому имя и семантику можно искать не зная типа.
  const itemStagesByType = data?.item_stages ?? {}
  const itemCatsByType = data?.item_categories ?? {}
  const itemStageById = new Map<string, RefStage>()
  for (const list of Object.values(itemStagesByType)) {
    for (const s of list) itemStageById.set(s.id, s)
  }
  const itemCatName = (etid: number | string, cid?: string | number | null) => {
    if (cid === undefined || cid === null || cid === '') return '—'
    const found = (itemCatsByType[String(etid)] ?? []).find((c) => c.id === String(cid))
    return found?.name || `Воронка ${cid}`
  }

  const stagesOfCategory = new Map<string, RefStage[]>()
  for (const s of stages) {
    const list = stagesOfCategory.get(s.category_id)
    if (list) list.push(s)
    else stagesOfCategory.set(s.category_id, [s])
  }

  const stageName: RefsApi['stageName'] = (stageId) => {
    if (!stageId) return '—'
    return stageById.get(String(stageId))?.name || String(stageId)
  }

  return {
    loaded: !!data,
    dictsStale: !!data?.dicts_stale,
    categories: [...(data?.categories ?? [])].sort(byNum).map((c) => ({ id: c.id, name: c.name })),
    users: [...(data?.users ?? [])]
      .map((u) => ({ id: String(u.id), name: fullName(u), active: u.active }))
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name, 'ru')),

    stageOptions(categoryId) {
      if (categoryId) {
        return [...(stagesOfCategory.get(categoryId) ?? [])]
          .sort(byNum)
          .map((s) => ({ id: s.id, code: s.code, name: s.name }))
      }
      // без воронки фильтруем по коду (%STAGE_ID), поэтому одинаковые коды разных воронок схлопываем
      const seen = new Map<string, StageOption>()
      for (const s of [...stages].sort(byNum)) {
        if (!seen.has(s.code)) seen.set(s.code, { id: '', code: s.code, name: s.name })
      }
      return [...seen.values()]
    },

    stageName,
    categoryName: (categoryId) => {
      if (categoryId === undefined || categoryId === null || categoryId === '') return '—'
      return categoryById.get(String(categoryId)) || `Воронка ${categoryId}`
    },
    // Три разных ответа, и подменять их друг другом нельзя:
    //   привязки нет            → «—»
    //   привязка есть, знаем    → фамилия и имя
    //   привязка есть, не знаем → «#123» (справочник не пришёл или сотрудника удалили)
    // Ноль — это «привязки нет»: Bitrix пишет отсутствие связи нулём, а не пустотой, и без
    // чистки через refId в карточках и списках печаталось «#0» — id, которого не существует.
    // Чистка одна на весь фронт (refId из lib/smart.ts), второй такой функции нет намеренно.
    userName: (id) => {
      const i = refId(id)
      return i ? (userById.get(i) || `#${i}`) : '—'
    },
    userNameOr: (id, fallback) => {
      const i = refId(id)
      const known = i ? userById.get(i) : undefined
      if (known) return known
      const свой = String(fallback ?? '').trim()
      if (свой) return свой
      return i ? `#${i}` : '—'
    },
    stageKind: (stageId) => {
      const sem = kindOfSemantics(stageById.get(String(stageId ?? ''))?.semantics)
      if (sem) return sem
      // справочника нет (или у стадии нет семантики) — судим по коду, как раньше
      const code = stageCode(stageId)
      if (code === 'WON') return 'won'
      if (code === 'LOSE' || code === 'APOLOGY') return 'lose'
      return 'work'
    },

    hasItemStages: (etid) => (itemStagesByType[String(etid)] ?? []).length > 0,

    itemCategories: (etid) =>
      [...(itemCatsByType[String(etid)] ?? [])].sort(byNum).map((c) => ({ id: c.id, name: c.name })),

    itemStageOptions: (etid, categoryId) =>
      [...(itemStagesByType[String(etid)] ?? [])]
        .filter((s) => !categoryId || s.category_id === categoryId)
        .sort(byNum)
        .map((s) => ({
          id: s.id, code: s.code, name: s.name,
          // без выбранной воронки в списке смешаны стадии всех воронок, а названия у них
          // повторяются («На сегодня» есть и в 27, и в 28) — иначе не отличить
          categoryName: categoryId ? undefined : itemCatName(etid, s.category_id),
        })),

    itemStageName: (stageId) => {
      if (!stageId) return '—'
      return itemStageById.get(String(stageId))?.name || String(stageId)
    },

    itemCategoryName: itemCatName,

    itemStageKind: (stageId) => {
      const sem = kindOfSemantics(itemStageById.get(String(stageId ?? ''))?.semantics)
      if (sem) return sem
      // Справочник стадий смарт-процессов не пришёл. Единственное, что можно вывести
      // из самого кода, — стандартные SUCCESS/FAIL: пользовательские стадии называются
      // UC_GAJKM4, и по такому коду о смысле судить нельзя.
      const code = stageCode(stageId)
      if (code === 'SUCCESS') return 'won'
      if (code === 'FAIL') return 'lose'
      return 'work'
    },
  }
}

/**
 * Собрать фильтр по стадии для запроса в ядро.
 * В Bitrix STAGE_ID зависит от воронки: в «Основной» (0) это `NEW`, в остальных — `C4:NEW`.
 * Поэтому при выбранной воронке фильтруем точным равенством, а без неё — подстрокой (%STAGE_ID),
 * иначе стадия из одной воронки не нашла бы сделки другой.
 */
export function stageFilter(code: string, categoryId: string): Record<string, string> {
  if (!code) return {}
  if (!categoryId) return { '%STAGE_ID': code }
  return { STAGE_ID: categoryId === '0' ? code : `C${categoryId}:${code}` }
}
